<script>
  import { format } from 'svelte-i18n';
  import { Button, Tooltip } from '@sveltestrap/sveltestrap';

  import { Icon } from '@client/components/icon';

  import { hasCapability } from '@client/stores/user';

  import { sanitizeRulenameOnKeyUp } from '@client/utils/sanitize';

  export let params = [];
  export let compliance = {};
  export let framed = true;
  export let namePlaceholder = 'label.NAME';
  export let valuePlaceholder = 'label.VALUE';

  const toggleFieldValue = (index, field) => {
    if (canEditDetails) {
      params[index][field] = !params[index][field];
      params = [...params];

      return true;
    }

    return false;
  };

  const addField = () => {
    params.push({
      name: '',
      default: '',
      forceoverride: true
    });

    params = [...params];
  };

  const removeField = (index) => {
    params.splice(index, 1);
    params = [...params];
  };

  $: canEditDetails = hasCapability('manage', 'manage:users');
  $: hasWarnings = params
    .map(
      (config) =>
        config?.name &&
        compliance?.userparams &&
        compliance.userparams[config.name] &&
        compliance.userparams[config.name] !== config.default &&
        config
    )
    .filter(Boolean).length;
</script>

<div class="fields" class:framed>
  {#each params as config, index}
    <div class="field columns">
      {#if config.forceoverride}
        <Icon
          name="lock"
          role="button"
          tabindex="0"
          id="param-field-{index}-lock"
          color="danger"
          class="text-medium {canEditDetails ? 'pointer' : ''}"
          on:click={() => toggleFieldValue(index, 'forceoverride')}
        />
        <Tooltip target="param-field-{index}-lock" placement="top">
          {$format('label.FIELD_LOCKED_BY_ADMIN')}
        </Tooltip>
      {:else}
        <Icon
          name="lock-open"
          role="button"
          tabindex="0"
          id="param-field-{index}-unlock"
          class="text-medium {canEditDetails ? 'pointer' : ''}"
          on:click={() => toggleFieldValue(index, 'forceoverride')}
        />
        <Tooltip target="param-field-{index}-unlock" placement="top">
          {$format('label.FIELD_CAN_BE_MODIFIED')}
        </Tooltip>
      {/if}

      {#if config.nosync}
        <Icon
          name="repeat-off"
          role="button"
          tabindex="0"
          id="param-field-{index}-no-sync"
          color="danger"
          class="text-medium {canEditDetails ? 'pointer' : ''}"
          on:click={() => toggleFieldValue(index, 'nosync')}
        />
        <Tooltip target="param-field-{index}-no-sync" placement="top">
          {$format('label.FIELD_IS_NOT_BEING_SYNCED')}
        </Tooltip>
      {:else}
        <Icon
          name="repeat"
          role="button"
          tabindex="0"
          id="param-field-{index}-sync"
          class="text-medium {canEditDetails ? 'pointer' : ''}"
          on:click={() => toggleFieldValue(index, 'nosync')}
        />
        <Tooltip target="param-field-{index}-sync" placement="top">
          {$format('label.FIELD_IS_BEING_SYNCED')}
        </Tooltip>
      {/if}

      {#if canEditDetails}
        <input
          type="text"
          class="name"
          placeholder={$format(namePlaceholder)}
          bind:value={config.name}
          on:keyup={() => (config.name = sanitizeRulenameOnKeyUp(config.name))}
        />
        <input type="text" class="value" placeholder={$format(valuePlaceholder)} bind:value={config.default} />
      {:else}
        <div class="name">{config.name}</div>
        <div class="value">{@html config.default || '&nbsp;'}</div>
      {/if}
      <Icon name="trash" role="button" tabindex="0" class="pointer" on:click={() => removeField(index)} />

      {#if hasWarnings}
        <div class="compliance">
          {#if config?.name && compliance?.userparams && compliance.userparams[config.name] && compliance.userparams[config.name] !== config.default}
            <Icon name="alert-triangle" id="param-field-{index}-compliance" color="danger" class="cursor-help" />

            <Tooltip target="param-field-{index}-compliance" placement="top">
              <!-- This user is using <b>{config.default}</b>
              as their
              <b>{config.name.length ? config.name : '<empty>'}</b>
              instead of
              <b>{compliance.userparams[config.name]}</b> -->
              {#if config.forceoverride}
                {@html $format('label.TOOLTIP.COMPLIANCE_WARNING_LOCKED', {
                  values: {
                    default: config.default,
                    name: config.name.length ? config.name : '<empty>',
                    configName: compliance.userparams[config.name]
                  }
                })}
              {/if}

              {#if !config.forceoverride}
                {@html $format('label.TOOLTIP.COMPLIANCE_WARNING_UNLOCKED', {
                  values: {
                    default: config.default,
                    name: config.name.length ? config.name : '<empty>',
                    configName: compliance.userparams[config.name]
                  }
                })}
              {/if}
            </Tooltip>
          {/if}
        </div>
      {/if}
    </div>
    <!-- {#if compliance.userparams[config.name] && compliance.userparams[config.name] !== config.default}
      <div class="compliance-flag">* {compliance.userparams[config.name]}</div>
    {/if} -->
  {/each}
  <Button color="secondary" size="sm" class="pointer {params.length ? 'mt-3' : ''}" on:click={addField}>
    <Icon name="plus" />
    <span>{$format('label.FIELD')}</span>
  </Button>
</div>

<style lang="scss" src="./param-fields.scss"></style>
